import type { MediaLinkPreview } from '../../api/mediaLinkPreview';

/**
 * Note that there may still be STAFF_ADDED records left over from an experiment around April, 2024. However, this code
 * now only supports staff adding MediaHits.
 */
type MediaSource = 'AUTOMATED' | 'STAFF_ADDED' | 'STARTER_ADDED';

export type MediaHitComment = { id: string; url: string; mediaSource: MediaSource };
export type MediaHitComments = MediaHitComment[];

/**
 * The resolution status of a media hit's iframely embed.
 * - `loading`: the embed has not finished resolving yet (or hasn't started).
 * - `valid`: iframely returned embeddable HTML.
 * - `invalid`: iframely failed to generate an embed (broken/removed URL, network failure, etc.).
 */
export type MediaHitEmbedStatus = 'loading' | 'valid' | 'invalid';

export type MediaHitEmbed = {
	id: string;
	url: string;
	mediaSource: MediaSource;
	status: MediaHitEmbedStatus;
	html: string;
	card: MediaLinkPreview | null;
};

/** Seeds a freshly-fetched hit into its pre-embed-resolution shape, shared so a future field can't half-land. */
export const toLoadingEmbed = ({ id, url, mediaSource }: MediaHitComment): MediaHitEmbed => ({
	id,
	url,
	mediaSource,
	status: 'loading',
	html: '',
	card: null,
});

export type MediaHitValues = {
	link: string;
};

export type MediaHitsContextValue = {
	data: {
		/**
		 * The media hits to render, in display order, each carrying its own embed-resolution status. Seeded (in a
		 * `loading` status) from the GQL `mediaHitsConnection` data and mutated as staff add (prepended) or remove hits.
		 * Embeds are not fetched on mount: `loadEmbeds` is called once the section nears the viewport, and each embed
		 * then resolves independently so one slow/broken embed never blocks the rest of the list.
		 */
		mediaEmbeds: MediaHitEmbed[];
		submitStatus: 'success' | 'error' | undefined;
	};
	actions: {
		/** Triggers the (one-time) fetch of every seeded embed. Safe to call repeatedly; only the first call loads. */
		loadEmbeds: () => void;
		addMedia: (id: string, url: string) => void;
		removeMedia: (mediaId: string) => void;
		setSubmitStatus: (status: 'success' | 'error' | undefined) => void;
	};
};
